import React, { useState } from 'react';
import LoginBox from './LoginBox';
import { useAuthenticateUser } from '../../hooks/useAuthenticateUser';
import { LoginState } from '../../types/Session';
import { useAppDispatch } from '../../support/store';
import { hideReloginModal } from '../../support/slices/uiSlice';

interface LoginProps {
  onLogin: () => void;
  onAuthStart?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, onAuthStart }) => {
  const dispatch = useAppDispatch();

  const {
    signinState,
    logout,
    startCiscoSSOLogin,
    clearAuthState,
  } = useAuthenticateUser();

  const [loading, setLoading] = useState(false);

  const handleLoginClick = () => {
    setLoading(true);
    dispatch(hideReloginModal());
    onAuthStart?.();

    localStorage.setItem('authProvider', 'salesforce');
    onLogin();
  };

  const handleCiscoSSOClick = async () => {
    setLoading(true);
    dispatch(hideReloginModal());
    onAuthStart?.();

    localStorage.setItem('authProvider', 'cisco_sso');

    // No default landing is seeded here: a postLoginPath set during a re-auth is preserved,
    // and a plain login falls through to the portal via HomeElement (see CiscoSSOCallback).

    try {
      await startCiscoSSOLogin();
    } catch (e: any) {
      console.error('[Login] Cisco SSO start failed', e);
      setLoading(false);
      alert(e?.message || 'Cisco SSO login failed to start');
    }
  };

  const handleClearSession = async () => {
    setLoading(true);
    try {
      await logout();
    } catch (e) {
      console.error('[Login] Clear Session failed', e);
      setLoading(false);
    }
  };

  const handleClearAuthState = () => {
    try {
      clearAuthState();
      localStorage.removeItem('authProvider');
      localStorage.removeItem('postLoginPath');
      window.location.href = '/login';
    } catch (e) {
      console.error('[Login] Clear Auth State failed', e);
      setLoading(false);
    }
  };

  const isDev =
    process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost';

  return (
    <>
      <LoginBox
        loading={signinState === LoginState.SigningIn || loading}
        onClick={handleLoginClick}
        onCiscoSSOClick={handleCiscoSSOClick}
        buttonText="Login"
        ciscoButtonText="Login with Cisco SSO"
        loadingText="Logging in..."
        showButton={true}
        showCiscoOption={true}
      />

      {isDev && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1001,
            display: 'flex',
            gap: 12,
          }}
        >
          <button type="button" onClick={handleClearSession}>
            Clear Session (Dev Only)
          </button>

          <button type="button" onClick={handleClearAuthState}>
            Clear Auth State
          </button>
        </div>
      )}
    </>
  );
};

export default Login;